import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  ArrowLeft, Zap, Target, Brain, Clock, Trophy, Star,
  Loader2, Play, RotateCcw, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const TimesTablesGamePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameType, tableNumber } = useParams();
  const { api, user } = useAuth();
  const isAdminPreview = location.pathname.includes('/admin/preview');
  const timerRef = useRef(null);
  
  const [gameState, setGameState] = useState('ready'); // ready, playing, finished
  const [questions, setQuestions] = useState([]);
  const [cards, setCards] = useState([]); // For memory match
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [loading, setLoading] = useState(true);

  const getBasePath = () => isAdminPreview ? '/admin/preview/times-tables' : '/student/times-tables';
  const tableNum = parseInt(tableNumber);

  const gameConfig = {
    speed: { name: 'Speed Challenge', icon: Zap, color: 'amber', timeLimit: 60, countdown: true },
    target: { name: 'Target Practice', icon: Target, color: 'emerald', timeLimit: null, questions: 10 },
    memory: { name: 'Memory Match', icon: Brain, color: 'purple', timeLimit: null },
    race: { name: 'Race Mode', icon: Clock, color: 'rose', timeLimit: 90, countdown: true }
  };

  const config = gameConfig[gameType] || gameConfig.speed;

  useEffect(() => {
    fetchQuestions();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchQuestions = async () => {
    try {
      const gameTypeMap = {
        speed: 'speed_challenge',
        target: 'target_practice',
        memory: 'memory_match',
        race: 'race_mode'
      };
      
      const response = await api.get(`/v2/times-tables/game/${gameTypeMap[gameType]}/${tableNumber}?count=20`);
      
      if (gameType === 'memory') {
        setCards(response.data.cards);
      } else {
        setQuestions(response.data.questions);
      }
      
      if (response.data.time_limit) {
        setTimeLeft(response.data.time_limit);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load game');
      navigate(getBasePath());
    } finally {
      setLoading(false);
    }
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setCorrect(0);
    setCurrentQuestion(0);
    setTimeElapsed(0);
    setFlippedCards([]);
    setMatchedPairs([]);
    
    if (config.countdown) {
      setTimeLeft(config.timeLimit);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      timerRef.current = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
  };

  const endGame = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setGameState('finished');
    
    const total = gameType === 'memory' ? matchedPairs.length : (gameType === 'speed' || gameType === 'race' ? currentQuestion : questions.length);
    const finalScore = gameType === 'speed' || gameType === 'race' ? correct : Math.round((correct / Math.max(total, 1)) * 100);
    
    // Record game result
    if (user?.id && !isAdminPreview) {
      try {
        const gameTypeMap = {
          speed: 'speed_challenge',
          target: 'target_practice',
          memory: 'memory_match',
          race: 'race_mode'
        };
        
        const response = await api.post(`/v2/times-tables/progress/${user.id}/record-game`, {
          table_number: tableNum,
          game_type: gameTypeMap[gameType],
          score: finalScore,
          correct: correct,
          total: total || 1,
          time_taken_seconds: config.countdown ? (config.timeLimit - timeLeft) : timeElapsed
        });
        
        if (response.data.mastered) {
          toast.success('🎉 You mastered this table! Next table unlocked!');
        }
        if (response.data.unlocked_next) {
          toast.success(`🔓 ${response.data.next_table}× table unlocked!`);
        }
      } catch (error) {
        console.error('Failed to record game:', error);
      }
    }
  }, [correct, currentQuestion, questions.length, matchedPairs.length, timeLeft, timeElapsed, gameType, user?.id, isAdminPreview, api, tableNum, config]);

  const handleAnswer = (answer) => {
    if (showResult) return;
    
    const question = questions[currentQuestion];
    const isCorrect = answer === question.answer;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    if (isCorrect) {
      setCorrect(prev => prev + 1);
      setScore(prev => prev + (gameType === 'speed' ? 1 : (gameType === 'race' ? Math.max(10 - Math.floor(timeElapsed / 10), 1) : 10)));
    }
    
    setTimeout(() => {
      setShowResult(false);
      setSelectedAnswer(null);
      
      if (gameType === 'target' && currentQuestion >= 9) {
        endGame();
      } else if (gameType === 'speed' || gameType === 'race') {
        setCurrentQuestion(prev => prev + 1);
        if (currentQuestion >= questions.length - 1) {
          fetchQuestions(); // Get more questions
        }
      } else {
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
        } else {
          endGame();
        }
      }
    }, isCorrect ? 500 : 1000);
  };

  const handleCardClick = (index) => {
    if (flippedCards.length === 2 || flippedCards.includes(index) || matchedPairs.includes(cards[index].match_id)) {
      return;
    }
    
    const newFlipped = [...flippedCards, index];
    setFlippedCards(newFlipped);
    
    if (newFlipped.length === 2) {
      const [first, second] = newFlipped;
      if (cards[first].match_id === cards[second].match_id) {
        setMatchedPairs(prev => [...prev, cards[first].match_id]);
        setCorrect(prev => prev + 1);
        setScore(prev => prev + 10);
        
        if (matchedPairs.length + 1 >= cards.length / 2) {
          setTimeout(() => endGame(), 500);
        }
      }
      setTimeout(() => setFlippedCards([]), 1000);
    }
  };

  const renderSpeedGame = () => {
    const question = questions[currentQuestion];
    if (!question) return null;
    
    return (
      <div className="text-center">
        <div className="text-6xl font-bold text-slate-800 mb-8">
          {question.equation} = ?
        </div>
        <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
          {[...Array(4)].map((_, i) => {
            const answer = question.options ? question.options[i] : question.answer + (i - 2) * tableNum;
            return (
              <button
                key={i}
                onClick={() => handleAnswer(answer)}
                disabled={showResult}
                className={`p-6 text-2xl font-bold rounded-2xl transition-all ${
                  showResult && answer === question.answer
                    ? 'bg-emerald-500 text-white scale-105'
                    : showResult && answer === selectedAnswer
                      ? 'bg-red-500 text-white'
                      : 'bg-white border-2 border-slate-200 hover:border-purple-400 hover:bg-purple-50'
                }`}
                data-testid={`answer-${i}`}
              >
                {answer}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTargetGame = () => {
    const question = questions[currentQuestion];
    if (!question) return null;
    
    return (
      <div className="text-center">
        <div className="text-5xl font-bold text-slate-800 mb-8">
          🎯 {question.equation} = ?
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {question.options?.map((option, i) => (
            <button
              key={i}
              onClick={() => handleAnswer(option)}
              disabled={showResult}
              className={`w-20 h-20 text-xl font-bold rounded-full transition-all transform hover:scale-110 ${
                showResult && option === question.answer
                  ? 'bg-emerald-500 text-white animate-bounce'
                  : showResult && option === selectedAnswer
                    ? 'bg-red-500 text-white'
                    : 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg hover:shadow-xl'
              }`}
              data-testid={`target-${i}`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderMemoryGame = () => {
    return (
      <div className="grid grid-cols-4 gap-3 max-w-lg mx-auto">
        {cards.map((card, index) => {
          const isFlipped = flippedCards.includes(index);
          const isMatched = matchedPairs.includes(card.match_id);
          
          return (
            <button
              key={index}
              onClick={() => !isFlipped && !isMatched && handleCardClick(index)}
              disabled={isMatched || flippedCards.length >= 2}
              className={`aspect-square rounded-xl text-lg font-bold transition-all transform ${
                isFlipped || isMatched
                  ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
                  : 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-400 hover:scale-105 cursor-pointer'
              } ${isMatched ? 'opacity-50 cursor-default' : ''}`}
              data-testid={`card-${index}`}
            >
              {(isFlipped || isMatched) ? card.value : '?'}
            </button>
          );
        })}
      </div>
    );
  };

  const renderRaceGame = () => {
    const question = questions[currentQuestion];
    if (!question) return null;
    
    return (
      <div className="text-center">
        <div className="mb-4">
          <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-rose-500 to-pink-600 transition-all"
              style={{ width: `${Math.max(0, (timeLeft / config.timeLimit) * 100)}%` }}
            />
          </div>
        </div>
        <div className="text-6xl font-bold text-slate-800 mb-8">
          {question.equation} = ?
        </div>
        <input
          type="number"
          autoFocus
          className="w-40 text-center text-4xl font-bold border-4 border-rose-300 rounded-2xl p-4 focus:border-rose-500 focus:outline-none"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAnswer(parseInt(e.target.value));
              e.target.value = '';
            }
          }}
          data-testid="race-input"
        />
        <p className="mt-4 text-slate-500">Press Enter to submit</p>
      </div>
    );
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
        </div>
      </StudentLayout>
    );
  }

  const Icon = config.icon;

  return (
    <StudentLayout>
      <div data-testid="times-tables-game" className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(getBasePath())}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Times Tables
        </button>

        {/* Header */}
        <div className={`student-card bg-gradient-to-r from-${config.color}-500 to-${config.color}-600 text-white mb-6`}
             style={{background: `linear-gradient(to right, var(--tw-gradient-from), var(--tw-gradient-to))`}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">{config.name}</h1>
                <p className="opacity-80">{tableNum}× Table</p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="text-right">
                <div className="text-4xl font-bold">{config.countdown ? timeLeft : score}</div>
                <div className="text-sm opacity-80">{config.countdown ? 'seconds' : 'points'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Game Area */}
        <div className="student-card min-h-[400px] flex flex-col justify-center">
          {gameState === 'ready' && (
            <div className="text-center">
              <Icon className={`w-20 h-20 text-${config.color}-500 mx-auto mb-6`} />
              <h2 className="text-2xl font-bold text-slate-800 mb-4">Ready to play?</h2>
              <p className="text-slate-600 mb-8">
                {gameType === 'speed' && 'Answer as many questions as you can in 60 seconds!'}
                {gameType === 'target' && 'Hit the correct targets! 10 questions await.'}
                {gameType === 'memory' && 'Match the equations with their answers!'}
                {gameType === 'race' && 'Race against the clock! Type your answers fast!'}
              </p>
              <Button 
                onClick={startGame}
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="start-game-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'playing' && (
            <>
              {gameType === 'speed' && renderSpeedGame()}
              {gameType === 'target' && renderTargetGame()}
              {gameType === 'memory' && renderMemoryGame()}
              {gameType === 'race' && renderRaceGame()}
            </>
          )}

          {gameState === 'finished' && (
            <div className="text-center">
              <Trophy className="w-20 h-20 text-amber-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Game Over!</h2>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto my-8">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-600">{score}</div>
                  <div className="text-sm text-slate-500">Score</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-3xl font-bold text-emerald-600">{correct}</div>
                  <div className="text-sm text-slate-500">Correct</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-600">
                    {gameType === 'memory' ? matchedPairs.length : currentQuestion}
                  </div>
                  <div className="text-sm text-slate-500">Answered</div>
                </div>
              </div>

              {/* Stars */}
              <div className="flex justify-center gap-2 mb-8">
                {[1, 2, 3].map((star) => {
                  const accuracy = correct / Math.max(currentQuestion || matchedPairs.length || 1, 1) * 100;
                  const earned = (star === 1 && accuracy >= 50) || (star === 2 && accuracy >= 75) || (star === 3 && accuracy >= 90);
                  return (
                    <Star 
                      key={star}
                      className={`w-12 h-12 ${earned ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                    />
                  );
                })}
              </div>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => navigate(getBasePath())}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => {
                  setGameState('ready');
                  fetchQuestions();
                }} className="bg-purple-600 hover:bg-purple-700">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Play Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default TimesTablesGamePage;
